import '../i18n/autoTranslate';

import React, { useState } from "react";
import AppBackground from '../../components/ui/AppBackground.tsx';

import SpotlightCard from "../../components/ui/SpotlightCard";
import Button from "../../components/ui/Button";
import { 
  Search, 
  BookOpen, 
  MessageCircle, 
  Phone, 
  Mail, 
  FileText, 
  Video, 
  Users, 
  ChevronRight,
  ExternalLink,
  CheckCircle
} from "lucide-react";

export default function Help() {
  const [activeSection, setActiveSection] = useState("getting-started");
  const [searchQuery, setSearchQuery] = useState("");

  const handleSectionClick = (section) => {
    setActiveSection(section);
  };

  // Sample FAQ data
  const faqCategories = [
    {
      id: "getting-started",
      title: "Getting Started",
      icon: <BookOpen className="h-5 w-5" />,
      questions: [
        {
          question: "How do I set up my account?",
          answer: "To set up your account, navigate to the settings page and complete your profile information. Make sure to verify your email address to unlock all features."
        },
        {
          question: "What are the system requirements?",
          answer: "Our platform works best on Chrome, Firefox, or Safari browsers. Ensure you have JavaScript enabled and a stable internet connection."
        },
        {
          question: "How do I import my data?",
          answer: "You can import data via the dashboard by clicking the 'Import' button and selecting your file. Supported formats include CSV, JSON, and Excel."
        }
      ]
    },
    {
      id: "billing",
      title: "Billing & Plans",
      icon: <FileText className="h-5 w-5" />,
      questions: [
        {
          question: "How does billing work?",
          answer: "Billing occurs monthly on the same date you signed up. You can upgrade, downgrade, or cancel at any time."
        },
        {
          question: "Can I change my plan?",
          answer: "Yes, you can change your plan at any time from the billing section in your account settings."
        }
      ]
    },
    {
      id: "troubleshooting",
      title: "Troubleshooting",
      icon: <CheckCircle className="h-5 w-5" />,
      questions: [
        {
          question: "What should I do if I can't log in?",
          answer: "Try resetting your password using the 'Forgot Password' link. If that doesn't work, contact our support team."
        },
        {
          question: "Why is my dashboard loading slowly?",
          answer: "This could be due to internet connectivity or browser cache. Try refreshing or clearing your browser cache."
        }
      ]
    }
  ];

  // Sample resources
  const resources = [
    {
      title: "Video Tutorials",
      description: "Step-by-step video guides",
      icon: <Video className="h-6 w-6" />,
      count: "15+ videos",
      link: "#"
    },
    {
      title: "Community Forum",
      description: "Connect with other users",
      icon: <Users className="h-6 w-6" />,
      count: "2k+ members",
      link: "#"
    },
    {
      title: "API Documentation",
      description: "Developer resources",
      icon: <FileText className="h-6 w-6" />,
      count: "Full API reference",
      link: "#"
    }
  ];

  // Sample contact methods
  const contactMethods = [
    {
      method: "Live Chat",
      description: "Instant support",
      icon: <MessageCircle className="h-5 w-5" />,
      availability: "24/7",
      responseTime: "< 2 min"
    },
    {
      method: "Email Support",
      description: "Detailed assistance",
      icon: <Mail className="h-5 w-5" />,
      availability: "24/7",
      responseTime: "< 4 hours"
    },
    {
      method: "Phone Support",
      description: "Direct conversation",
      icon: <Phone className="h-5 w-5" />,
      availability: "Mon-Fri, 9AM-6PM EST",
      responseTime: "Immediate"
    }
  ];

  return (
    <AppBackground className="min-h-screen bg-[#0B0B12] text-white relative overflow-hidden">
      <main className="pt-10 pb-24 px-4 sm:px-6 lg:px-8 relative z-10">
        
        {/* Hero Section */}
        <div className="max-w-4xl mx-auto text-center mb-12 relative">
          <div className="flex items-center justify-center mb-5">
            <div className="flex items-center justify-center w-12 h-12 rounded-2xl bg-gradient-to-r from-indigo-500/20 via-fuchsia-500/15 to-cyan-400/15 border border-white/10 shadow-[0_0_22px_rgba(99,102,241,0.18)]">
              <BookOpen className="h-6 w-6 text-white" />
            </div>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent mb-3">
            How can we help you?
          </h1>
          <p className="text-base md:text-lg text-white/70 mb-8">
            Find answers, guides, and resources to get the most out of our platform.
          </p>
          
          {/* Search Bar */}
          <div className="max-w-2xl mx-auto relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-white/50 h-5 w-5" />
            <input
              type="text"
              placeholder="Search for answers..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-4 bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:border-white/10"
            />
          </div>
        </div>

        <div className="max-w-6xl mx-auto flex flex-col lg:flex-row gap-8">
          {/* Sidebar Navigation */}
          <div className="w-full lg:w-64 flex-shrink-0">
            <SpotlightCard className="custom-spotlight-card rounded-3xl overflow-hidden sticky top-6" spotlightColor="rgba(99, 102, 241, 0.12)">
              <div className="bg-gradient-to-br from-[rgba(30,30,46,0.70)] via-[rgba(30,30,46,0.55)] to-[rgba(46,46,69,0.30)] backdrop-blur-md border border-white/10 rounded-3xl p-4 lg:p-6">
                <h3 className="font-semibold text-lg mb-4">
                  <span className="bg-gradient-to-br from-[#8B7CFF] to-[#6C63FF] bg-clip-text text-transparent">
                    Help Categories
                  </span>
                </h3>
                <nav className="space-y-2">
                  {faqCategories.map((category) => (
                    <button
                      key={category.id}
                      onClick={() => handleSectionClick(category.id)}
                      className={`w-full flex items-center space-x-3 px-3 py-3 rounded-2xl text-left transition-all duration-200 border ${
                        activeSection === category.id
                          ? "relative overflow-hidden bg-gradient-to-br from-[#8B7CFF] to-[#6C63FF] text-white font-medium border-white/10 shadow-[0_0_22px_rgba(99,102,241,0.18)] hover:shadow-[0_0_50px_rgba(108,99,255,0.40)] before:content-[''] before:absolute before:-bottom-1.5 before:left-[20%] before:w-1/2 before:h-4 before:rounded-full before:bg-white/70 before:blur-[10px] before:pointer-events-none"
                          : "text-white/75 bg-white/5 border-white/10 hover:bg-white/10"
                      }`}
                    >
                      <span className={`${activeSection === category.id ? "text-white" : "text-indigo-300"}`}>{category.icon}</span>
                      <span className="font-medium">{category.title}</span>
                    </button>
                  ))}
                </nav>

                <div className="mt-8 pt-6 border-t border-white/10">
                  <h3 className="font-semibold text-lg text-white mb-4">Quick Resources</h3>
                  <div className="space-y-3">
                    {resources.map((resource, index) => (
                      <a
                        key={index}
                        href={resource.link}
                        className="flex items-center justify-between p-3 rounded-2xl border border-white/10 bg-white/5 hover:bg-white/10 transition-colors duration-200 group"
                      >
                        <div className="flex items-center space-x-3">
                          <span className="text-indigo-300">{resource.icon}</span>
                          <div>
                            <div className="font-medium text-sm text-white">{resource.title}</div>
                            <div className="text-xs text-white/50">{resource.count}</div>
                          </div>
                        </div>
                        <ExternalLink className="h-4 w-4 text-white/50 group-hover:text-white transition-colors" />
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            </SpotlightCard>
          </div>

          {/* Main Content */}
          <div className="flex-1">
            <SpotlightCard
              className="custom-spotlight-card rounded-3xl overflow-hidden"
              spotlightColor="rgba(99, 102, 241, 0.15)"
            >
              <div className="bg-gradient-to-br from-[rgba(30,30,46,0.80)] via-[rgba(30,30,46,0.70)] to-[rgba(46,46,69,0.40)] backdrop-blur-md border border-white/10 rounded-3xl">
                
                {/* FAQ Section */}
                <div className="p-6 sm:p-8">
                  <div className="flex items-center justify-between mb-6 gap-4">
                    <h2 className="text-2xl font-bold flex items-center text-white">
                      <span className="text-indigo-300">
                        {faqCategories.find(cat => cat.id === activeSection)?.icon}
                      </span>
                      <span className="ml-3">
                        {faqCategories.find(cat => cat.id === activeSection)?.title}
                      </span>
                    </h2>
                    <span className="hidden sm:inline-flex text-xs px-3 py-1 rounded-full border border-white/10 bg-white/5 text-white/60">
                      {faqCategories.find(cat => cat.id === activeSection)?.questions?.length || 0} articles
                    </span>
                  </div>
                  
                  <div className="space-y-4">
                    {faqCategories
                      .find(cat => cat.id === activeSection)
                      ?.questions.map((faq, index) => (
                        <div key={index} className="bg-white/5 border border-white/10 rounded-2xl p-4 hover:bg-white/10 transition-colors duration-200">
                          <h3 className="font-semibold text-lg mb-2 text-white">{faq.question}</h3>
                          <p className="text-white/70 text-sm">{faq.answer}</p>
                        </div>
                      ))}
                  </div>
                </div>

                {/* Contact Section */}
                <div className="border-t border-white/10 p-6 sm:p-8">
                  <h2 className="text-2xl font-bold mb-2 text-white">Still need help?</h2>
                  <p className="text-white/70 mb-6">Contact support and we’ll help you get unblocked.</p>
                  <div className="grid md:grid-cols-3 gap-6">
                    {contactMethods.map((method, index) => (
                      <div key={index} className="bg-white/5 border border-white/10 rounded-2xl p-6 hover:bg-white/10 transition-all duration-200 hover:transform hover:scale-[1.02]">
                        <div className="flex items-center space-x-3 mb-4">
                          <div className="p-2 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-2xl border border-white/10 shadow-[0_0_22px_rgba(99,102,241,0.18)]">
                            {method.icon}
                          </div>
                          <div>
                            <h3 className="font-semibold text-white">{method.method}</h3>
                            <p className="text-sm text-white/60">{method.description}</p>
                          </div>
                        </div>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-white/50">Availability:</span>
                            <span className="text-white/80">{method.availability}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-white/50">Response time:</span>
                            <span className="text-emerald-300">{method.responseTime}</span>
                          </div>
                        </div>
                        <div className="mt-4">
                          <Button fullWidth iconName="ArrowRight" iconPosition="right">
                            Contact {method.method}
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Quick Actions */}
                <div className="border-t border-white/10 p-6 sm:p-8">
                  <h2 className="text-2xl font-bold mb-6 text-white">Quick Actions</h2>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <button className="flex items-center justify-between p-4 bg-white/5 border border-white/10 rounded-2xl hover:bg-white/10 transition-colors duration-200 group">
                      <div className="flex items-center space-x-3">
                        <FileText className="h-5 w-5 text-indigo-400" />
                        <span className="text-white">Download User Guide</span>
                      </div>
                      <ChevronRight className="h-4 w-4 text-white/60 group-hover:translate-x-1 transition-transform" />
                    </button>
                    <button className="flex items-center justify-between p-4 bg-white/5 border border-white/10 rounded-2xl hover:bg-white/10 transition-colors duration-200 group">
                      <div className="flex items-center space-x-3">
                        <Video className="h-5 w-5 text-indigo-400" />
                        <span className="text-white">Watch Tutorials</span>
                      </div>
                      <ChevronRight className="h-4 w-4 text-white/60 group-hover:translate-x-1 transition-transform" />
                    </button>
                  </div>
                </div>
              </div>
            </SpotlightCard>
          </div>
        </div>
      </main>
    </AppBackground>
  );
}
